import { getOrMakeUnitDefinitionFromMdsSiMarkup } from '@metservicedev/cf-units';
import React from 'react';
import { useMDSContext } from '@metservice/use-mds';
// import { DateTime } from 'luxon';

import aggregateFunctions from '../../../utils/named-aggregate-functions';
import { DataTableDiv, ThreatCell } from '../DataTable.style';
import DataTableThreatLegend from '../DataTableThreatLegend';
import { DataTableProps } from '../types';
import { filterDatasetsAndCapabilities, getCellThreatLimit, getCellText, getDisplayValue, getNumberOfDatumsInDay, renderVerticalHeading } from './utils';

const DataTableVertical = ({
    title,
    datasets,
    reportTime,
    databilities,
    arrayOfTimes,
    headings,
    fontSize,
    showGroupLabels,
    binning,
    bins,
    rotateVerticalLabels,
    threatLimits,
    limits,
    displayTableHeading,
    headerPadding,
}: DataTableProps) => {
    const { capabilities } = useMDSContext();
    const maxArrayOfTimesLength = 192; // 24 values a day, 8 days
    const truncatedArrayOfTimes = arrayOfTimes.length > maxArrayOfTimesLength;
    const reportTZ = reportTime?.tz();

    const days = Array.from(new Set(arrayOfTimes.map((time) => time.clone().startOf('day').toISOString())));

    return (
        <DataTableDiv className='dataTableRender vertical-dataTable' fontSize={fontSize} headerPadding={headerPadding}>
            {truncatedArrayOfTimes && <div>Too much data to display, data has been truncated</div>}
            <div className='tableWrapper'>
                <table>
                    {displayTableHeading && (
                        <thead>
                            <tr className='heading' key='tr-heading'>
                                <th colSpan={2} className='bordered'>
                                    {title}
                                </th>
                                {headings.map((heading) => {
                                    if (showGroupLabels) {
                                        return (
                                            <th key={heading.id} colSpan={heading.databilities.length || 1} className='bordered'>
                                                {heading.label}
                                            </th>
                                        );
                                    }

                                    if (heading.databilities.length === 0) {
                                        return (
                                            <th key={heading.id} rowSpan={2} className='bordered'>
                                                {'-'}
                                            </th>
                                        );
                                    }

                                    return renderVerticalHeading({ heading, rowSpan: 2, databilities, datasets, rotateVerticalLabels });
                                })}
                            </tr>
                            <tr key='tr-report-time'>
                                <th colSpan={2} className='bordered heading'>
                                    {`${reportTime?.format('z')} (UTC ${reportTime?.format('Z')})`}
                                </th>
                                {showGroupLabels &&
                                    headings.map((heading) => {
                                        if (heading.databilities.length === 0) {
                                            return (
                                                <th key={heading.id} rowSpan={showGroupLabels ? 1 : 2} className='bordered'>
                                                    -
                                                </th>
                                            );
                                        }
                                        return renderVerticalHeading({ heading, rowSpan: 1, databilities, datasets, rotateVerticalLabels });
                                    })}
                            </tr>
                        </thead>
                    )}
                    <tbody>
                        {arrayOfTimes.map((time, key) => {
                            const previousTime = arrayOfTimes[key - 1];
                            const nextTime = arrayOfTimes[key + 1];
                            const isStartOfDay = key === 0 || time.clone().startOf('day').toISOString() !== previousTime.clone().startOf('day').toISOString();
                            const numberOfDatumsInDay =
                                key === 0 ? getNumberOfDatumsInDay(false, bins, time, arrayOfTimes) : getNumberOfDatumsInDay(binning, bins, time, arrayOfTimes);

                            const dayIndex = days.findIndex((d) => d === time.clone().startOf('day').toISOString());

                            let isEndOfDay = false;

                            if (key < arrayOfTimes.length - 1) {
                                isEndOfDay = time.clone().startOf('day').unix() !== nextTime.clone().startOf('day').unix();
                            } else if (key === arrayOfTimes.length - 1) {
                                isEndOfDay = true;
                            }

                            const addedClasses = [];

                            if (isEndOfDay) {
                                addedClasses.push('endOfDayRow');
                            }

                            if (dayIndex % 2 === 1) {
                                addedClasses.push('oddDay');
                            }

                            return (
                                <tr key={`tr-${time.unix()}`} className={addedClasses.join(' ')}>
                                    {isStartOfDay && (
                                        <td rowSpan={numberOfDatumsInDay} className='bordered heading date-label'>
                                            <div className={rotateVerticalLabels ? 'rotate-this-outer' : undefined}>
                                                <div className={rotateVerticalLabels ? 'rotate-this-vertical' : undefined}>
                                                    <div>{time.format('ddd')}</div>
                                                    <div>{time.format('DD MMM')}</div>
                                                </div>
                                            </div>
                                        </td>
                                    )}
                                    <td className='bordered heading'>
                                        <div>{time.format('HH:mm')}</div>
                                    </td>
                                    {headings
                                        .map((heading) => {
                                            const searchIndexes = Array(headings.length).fill(0);

                                            if (heading.databilities.length === 0) {
                                                return [
                                                    <td key={`emptyCol - ${heading.id}`} className={'lastElementInColumn'}>
                                                        -
                                                    </td>,
                                                ];
                                            }

                                            return heading.databilities.map((datability, databilityKey) => {
                                                const binFunction = binning
                                                    ? aggregateFunctions[heading.aggregateFunctions[databilityKey] || 'first'].f
                                                    : (x: number[]) => x;
                                                const addedClass = databilityKey === heading.databilities.length - 1 ? ' lastElementInColumn' : '';

                                                const { wholeDataset, wholeCapability, capUnit } = filterDatasetsAndCapabilities({
                                                    databilities,
                                                    databilityId: datability,
                                                    datasets,
                                                });
                                                const unit =
                                                    (heading.databilityUnits && heading.databilityUnits[databilityKey]) ||
                                                    getOrMakeUnitDefinitionFromMdsSiMarkup(capUnit).unit;

                                                const displayValue = getDisplayValue({
                                                    time,
                                                    fromIndex: searchIndexes[databilityKey],
                                                    reportTZ,
                                                    wholeDataset,
                                                    wholeCapability,
                                                    binning,
                                                    bins,
                                                    dp: heading.dp[databilityKey],
                                                    direction: heading.directions[databilityKey],
                                                    binFunction,
                                                    unit,
                                                    directionalVisualization: heading.directionalVisualization[databilityKey],
                                                    vectorNormalization: heading.vectorNormalization[databilityKey],
                                                    capabilities,
                                                });

                                                const cellThreatLimit = getCellThreatLimit({
                                                    heading: heading,
                                                    index: databilityKey,
                                                    threatLimits,
                                                    limits,
                                                    value: displayValue.displayValue,
                                                });

                                                const cellText = getCellText({ cellThreatLimit, displayValue });
                                                const colour = heading.colours[databilityKey];

                                                return (
                                                    <ThreatCell
                                                        className={addedClass}
                                                        key={`${time.unix()}${heading.id}${databilityKey}`}
                                                        bgColor={cellThreatLimit?.backgroundColor}
                                                        defaultColour={colour}
                                                    >
                                                        {cellText}
                                                    </ThreatCell>
                                                );
                                            });
                                        })
                                        .reduce((prev, current) => prev.concat(current), [])}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <DataTableThreatLegend threatLimits={threatLimits} fontSize={fontSize} />
        </DataTableDiv>
    );
};

export default DataTableVertical;
